<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const dialogRef = ref(null);

const deleting = ref(false);
const deleteError = ref('');
const contactId = computed(
  () => route.params.contactId || props.selectedContact?.id
);
let contextVersion = 0;
watch([() => route.params.accountId, contactId], () => {
  contextVersion += 1;
  deleteError.value = '';
  dialogRef.value?.close();
});
onBeforeUnmount(() => {
  contextVersion += 1;
});

const handleDialogConfirm = async () => {
  if (deleting.value || !contactId.value) return;
  const version = contextVersion;
  const id = contactId.value;
  deleting.value = true;
  deleteError.value = '';
  try {
    await store.dispatch('contacts/delete', id);
    if (version !== contextVersion) return;
    useAlert(t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.API.SUCCESS_MESSAGE'));
    dialogRef.value?.close();
    emit('goToContactsList');
  } catch {
    if (version !== contextVersion) return;
    deleteError.value = t(
      'CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.API.ERROR_MESSAGE'
    );
  } finally {
    deleting.value = false;
  }
};

const requestClose = () => {
  if (!deleting.value) dialogRef.value?.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    :title="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.TITLE')"
    :description="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.DESCRIPTION')"
    :confirm-button-label="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.CONFIRM')"
    :is-loading="deleting"
    :confirm-before-close="deleting"
    @confirm="handleDialogConfirm"
    @request-close="requestClose"
    @close="deleteError = ''"
  >
    <p v-if="deleteError" role="alert" class="mb-0 text-sm text-n-ruby-11">
      {{ deleteError }}
    </p>
  </Dialog>
</template>
